import usePackageLimits from "@/hooks/usePackageLimits";
import {useIsAgency, useIsCompany, useIsLoggedIn, useIsSubbrand} from "@/hooks/useUserHooks";
import useUserStore from "@/store/user";
import dayjs from "dayjs";
import {useEffect, useMemo, useState} from "react";
import {DataAttributesCompany, useIntercom} from "react-use-intercom";

const useInternalIntercom = () => {
  const isLoggedin = useIsLoggedIn();
  const user = useUserStore(state => state.user);
  const isAgency = useIsAgency();
  const isSubbrand = useIsSubbrand();
  const isCompany = useIsCompany();
  const {update, boot} = useIntercom();
  const [booted, setBooted] = useState(false);
  const {remainingBuyouts} = usePackageLimits();

  useEffect(() => {
    if (isLoggedin && !!user.id) {
      boot();
      setBooted(true);
    }
  }, [boot, isLoggedin, user.id]);

  const companies = useMemo((): DataAttributesCompany[] => {
    if (!isLoggedin || !user) return [];

    // 1. Agency members Intercom company, Agency + Subbrands
    if (isAgency) {
      const agencyCompany = user.agencies.map<DataAttributesCompany>(a => ({
        companyId: "agency-" + a.agency.id?.toString()!,
        name: a.agency.name,
        customAttributes: {
          agencyId: a.agency.id,
          name: a.agency.name,
        },
      }));
      const agencySubbrands = user.sub_brands?.map<DataAttributesCompany>(s => ({
        companyId: "subbrand-" + s.company.id?.toString()!,
        name: s.company.company_name!,
        customAttributes: {
          subbrandId: s.company.id,
          name: s.company.company_name,
        },
      }));

      return [...agencyCompany, ...(agencySubbrands || [])];
    }
    // 2. Agency's subrand Intercom company (subbrand members)
    if (isSubbrand) {
      const subbrands = user.sub_brands?.map<DataAttributesCompany>(s => ({
        companyId: "subbrand-" + s.company.id?.toString()!,
        name: s.company.company_name!,
        customAttributes: {
          subbrandId: s.company.id,
          name: s.company.company_name,
        },
      }));

      return subbrands || [];
    }
    // 3. Company members Intercom company
    if (isCompany) {
      const companies = user.sub_brands?.map<DataAttributesCompany>(s => ({
        companyId: "company-" + s.company.id?.toString()!,
        name: s.company.company_name!,
        customAttributes: {
          companyId: s.company.id,
          name: s.company.company_name!,
        },
      }));

      return companies || [];
    }

    return [];
  }, [isAgency, isSubbrand, isCompany]);

  useEffect(() => {
    if (isLoggedin && !!user.id && booted) {
      const planQuestions = {
        refluenced_how_long_is_my_plan_valid: dayjs(user.package_subscription?.valid_until).format("DD. MMMM YYYY."),
        refluenced_when_does_my_current_plan_expire: dayjs(user.package_subscription?.valid_until).format(
          "DD. MMMM YYYY.",
        ),
        refluenced_how_many_content_buyouts_do_i_have_left: remainingBuyouts,
        refluenced_how_many_campaigns_can_i_launch_with_my_plan: user.package_subscription?.number_of_campaigns,
        refluenced_is_there_a_limit_to_how_many_influencers_i_can_collaborate_with_per_campaign:
          user.package_subscription?.influencers_per_campaign,
        refluenced_how_many_influencer_requests_can_i_send: user.package_subscription?.invitations_per_campaign,
        refluenced_can_i_run_multiple_campaigns_simultaneously: user.package_subscription?.multiple_active_campaigns,
        refluenced_pkg_is_admin_assigned: user.package_subscription?.is_admin_assigned,
      };

      update({
        name: user.first_name,
        email: user.email,
        userId: user.id?.toString(),
        companies,
        languageOverride: user.language_code!,
        customAttributes: {
          ...planQuestions,
        },
      });
    }
  }, [user, isLoggedin, companies, booted, user, remainingBuyouts]);
};

export {useInternalIntercom};
